import { useState, useEffect, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { PiMagnifyingGlassBold } from "react-icons/pi";

import API from "../../api";
import toast from "react-hot-toast";
import { capture } from "../../instrument";

const QuestionSearchInput = ({ onSelect, disabled = false }) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setIsLoading(true);
        const { ok, data, message } = await API.get(`/question?q=${encodeURIComponent(query.trim())}`);
        if (!ok) throw new Error(message);
        setSuggestions(data);
        setSelectedIndex(0);
      } catch (error) {
        capture(error);
        toast.error(error.message || "Une erreur est survenue lors de la recherche");
      } finally {
        setIsLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  const navOpts = { enabled: suggestions.length > 0, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys("down", () => setSelectedIndex((i) => Math.min(i + 1, suggestions.length - 1)), navOpts, [suggestions]);
  useHotkeys("up", () => setSelectedIndex((i) => Math.max(i - 1, 0)), navOpts, [suggestions]);
  useHotkeys("enter", () => suggestions[selectedIndex] && onSelect(suggestions[selectedIndex]), navOpts, [suggestions, selectedIndex]);

  return (
    <div className="relative w-full">
      <div className="field-shell" onClick={() => inputRef.current?.focus()}>
        <PiMagnifyingGlassBold className="h-3.5 w-3.5 shrink-0 text-gray-400" />
        <input
          ref={inputRef}
          autoFocus
          disabled={disabled}
          className="h-full flex-1 bg-transparent text-[13px] text-gray-800 outline-none placeholder:text-gray-300"
          placeholder="Rechercher une question à ajouter..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {isLoading && <div className="field-spinner" />}
      </div>

      {suggestions.length > 0 && (
        <div className="dropdown-panel absolute z-50 mt-1 max-h-60 w-full overflow-auto py-1">
          {suggestions.map((question, index) => (
            <button
              key={question._id}
              type="button"
              className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left transition-colors focus:outline-none ${
                index === selectedIndex ? "bg-primary-50 text-primary-700" : "text-gray-700 hover:bg-gray-50"
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(question);
              }}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="truncate text-[13px] font-medium">{question.question || question.questionCode}</span>
              {question.questionCode && <span className="truncate font-mono text-[11px] text-gray-400">{question.questionCode}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuestionSearchInput;
